import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatNzDate, nzDate } from '@promanage/core';
import { Button } from '@promanage/ui';
import { DataTable } from '../features/table/DataTable';
import { EmptyState, FilterChips, ListShell } from '../features/table/ListShell';
import { compareRows, type ColumnDef, type SortState } from '../features/table/columns';
import { ListVisibility } from '../features/lists/ListVisibility';
import { CommunicationsNav } from '../features/lists/CommunicationsNav';
import { trpc, type RouterOutputs } from '../lib/trpc';

export const Route = createFileRoute('/communications/')({
  loader: ({ context }) => context.queryClient.prefetchQuery(listsOptions()),
  component: CommunicationsPage,
});

const listsOptions = () => trpc.lists.list.queryOptions(undefined, { staleTime: 10_000 });

type SavedListSummary = RouterOutputs['lists']['list'][number];
type Column = 'name' | 'base' | 'visibility' | 'updated';
type Filter = 'all' | 'mine' | 'shared';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'Mine' },
  { value: 'shared', label: 'Shared' },
];

const NO_LISTS: SavedListSummary[] = [];

/*
 * Base labels written out rather than imported: `@promanage/core/lists` carries the whole field
 * registry, and the index has no use for it — it belongs to the list page's own chunk.
 */
const BASE_LABELS: Record<SavedListSummary['base'], string> = {
  tenancies: 'Tenancies',
  owners: 'Owners',
};

const COLUMNS: readonly ColumnDef<Column, SavedListSummary>[] = [
  {
    id: 'name',
    label: 'List',
    width: '28rem',
    fixed: true,
    defaultVisible: true,
    sortValue: (list) => list.name.toLowerCase(),
    render: (list) => (
      <div className="min-w-0">
        <Link
          to="/communications/lists/$id"
          params={{ id: list.id }}
          className="block truncate font-semibold hover:text-accent hover:underline"
        >
          {list.name}
        </Link>
        <span className="block truncate text-[0.75rem] text-ink-subtle" title={list.sentence}>
          {list.sentence}
        </span>
      </div>
    ),
  },
  {
    id: 'base',
    label: 'Lists',
    width: '8rem',
    defaultVisible: true,
    sortValue: (list) => list.base,
    render: (list) => BASE_LABELS[list.base],
  },
  {
    id: 'visibility',
    label: 'Who sees it',
    width: '12rem',
    defaultVisible: true,
    sortValue: (list) => (list.shared ? `1${list.authorName}` : '0'),
    render: (list) => (
      <ListVisibility shared={list.shared} mine={list.mine} authorName={list.authorName} />
    ),
  },
  {
    id: 'updated',
    label: 'Changed',
    width: '8rem',
    defaultVisible: true,
    sortValue: (list) => list.updatedAt,
    render: (list) => (
      <span className="tabular-nums">{formatNzDate(nzDate(new Date(list.updatedAt)))}</span>
    ),
  },
];

/**
 * Communications — the agency's saved lists, and (next) what is sent to them.
 *
 * A list is a filter over tenancies or owners that somebody wanted to come back to: "fixed terms
 * ending in the next 60 days", "owners in Henderson". Shared lists are the agency's; personal ones
 * are the reader's own, and nobody else's appear here at all.
 */
function CommunicationsPage() {
  const navigate = useNavigate();
  const lists = useQuery({ ...listsOptions(), meta: { errorTitle: 'Could not load the lists' } });
  const all = lists.data ?? NO_LISTS;

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<SortState<Column>>({ column: 'name', direction: 'asc' });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all
      .filter((list) => (filter === 'mine' ? list.mine : filter === 'shared' ? list.shared : true))
      .filter((list) => !q || `${list.name} ${list.sentence}`.toLowerCase().includes(q))
      .sort((a, b) => compareRows(a, b, sort, COLUMNS));
  }, [all, query, filter, sort]);

  const newList = (
    <Link to="/communications/lists/new">
      <Button size="sm">
        <span aria-hidden="true">+</span> New list
      </Button>
    </Link>
  );

  return (
    <ListShell
      title="Communications"
      subtitle={
        lists.isPending
          ? 'Loading…'
          : rows.length === all.length
            ? `${all.length} saved ${all.length === 1 ? 'list' : 'lists'}`
            : `Showing ${rows.length} of ${all.length} lists`
      }
      nav={<CommunicationsNav current="lists" />}
      actions={newList}
      search={query}
      onSearchChange={setQuery}
      searchPlaceholder="Search list names and what they select"
      searchLabel="Search lists"
      filters={
        <FilterChips label="Filter lists" value={filter} options={FILTERS} onChange={setFilter} />
      }
      isPending={lists.isPending}
      error={lists.error}
      onRetry={() => void lists.refetch()}
      isEmpty={rows.length === 0}
      empty={
        <EmptyState
          title={query ? `Nothing matches “${query}”` : 'No lists yet'}
          {...(query ? {} : { action: newList })}
        >
          {query
            ? 'Try a different name.'
            : 'A list picks out tenancies or owners by what is true of them — a suburb, a start date, a fixed term ending soon — and keeps the filter to come back to.'}
        </EmptyState>
      }
    >
      <DataTable
        rows={rows}
        columns={COLUMNS}
        sort={sort}
        onSortChange={setSort}
        rowKey={(list) => list.id}
        onOpen={(list) =>
          void navigate({ to: '/communications/lists/$id', params: { id: list.id } })
        }
        caption={`Saved lists, one row per list. ${rows.length} shown.`}
        minWidth="44rem"
      />
    </ListShell>
  );
}
